import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toIsoDate, fmtDateShortYear, fmtDateMedium, fmtWeekdayShort, fmtDateTimeShort, syncDateRange } from '../utils/dateUtils';
import useIsMobile from '../hooks/useIsMobile';

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtDate = (v) => fmtDateShortYear(v);
const fmtDateFull = (v) => fmtDateMedium(v);
const dayLabel = (v) => fmtWeekdayShort(v);

/* reusable inline-only styles (everything else uses CSS classes) */
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 };
const modalStyle = { width: 'min(660px,96vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.2rem', position: 'relative' };
const closeBtnStyle = { position: 'absolute', top: '0.5rem', right: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 16, padding: '2px 8px' };

function PmsRoomInventory({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [tab, setTab] = useState('grid');
  const [startDate, setStartDate] = useState(toIsoDate(new Date()));
  const [days, setDays] = useState(14);
  const [matrixData, setMatrixData] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true); // REQ-07a: live grid refresh
  const [lastUpdated, setLastUpdated] = useState(null);

  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);

  const [bulkModal, setBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    room_type_id: '', start_date: '', end_date: '', day_filter: 'all', reason: '',
    stop_sell_flag: false, cta_flag: false, ctd_flag: false,
    overbooking_limit: 0, out_of_service: 0, house_use: 0, min_los: 1, max_los: 0
  });
  const [bulkBusy, setBulkBusy] = useState(false);

  const [auditRows, setAuditRows] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilter, setAuditFilter] = useState({ room_type_id: '', from: '', to: '', source: '' });
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(0);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const endDate = useMemo(() => toIsoDate(addDays(startDate, days - 1)), [startDate, days]);
  const dateColumns = useMemo(() => {
    const cols = [];
    for (let i = 0; i < days; i++) cols.push(toIsoDate(addDays(startDate, i)));
    return cols;
  }, [startDate, days]);

  /* ── load room types from master (independent of matrix) ── */
  const loadRoomTypes = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/pms/room-types', { headers: headers() });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        const rts = data.filter(rt => rt.is_active !== false).map(rt => ({
          room_type_id: Number(rt.room_type_id),
          room_type_name: rt.room_type_name || `Type ${rt.room_type_id}`,
          room_type_code: rt.room_type_code || '',
          total_rooms: Number(rt.total_rooms || 0)
        }));
        rts.sort((a, b) => a.room_type_name.localeCompare(b.room_type_name));
        setRoomTypes(rts);
      }
    } catch (_) { /* room types will fall back to matrix-derived if this fails */ }
  }, [selectedProperty, headers]);

  useEffect(() => { loadRoomTypes(); }, [loadRoomTypes]);

  /* ── load matrix ── */
  const loadMatrix = useCallback(async (silent) => {
    if (!selectedProperty?.id) return;
    if (!silent) { setLoading(true); setError(''); }
    try {
      const res = await fetch(`/api/v1/pms/availability/matrix?from=${startDate}&to=${endDate}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setMatrixData(data);
      setLastUpdated(new Date());
    } catch (err) { if (!silent) setError(err?.message || 'Failed to load inventory'); }
    finally { if (!silent) setLoading(false); }
  }, [selectedProperty, startDate, endDate, headers]);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  // REQ-07a: live refresh — silently re-pull the matrix every 30s while the grid
  // tab is open and the browser tab is visible. Toggle off via the "Live" switch.
  useEffect(() => {
    if (!autoRefresh || tab !== 'grid') return undefined;
    const id = setInterval(() => { if (!document.hidden) loadMatrix(true); }, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, tab, loadMatrix]);

  const getCell = useCallback((roomTypeId, date) => {
    if (!matrixData?.by_room_type) return null;
    const rows = matrixData.by_room_type[roomTypeId];
    return rows?.find(r => r.date === date) || null;
  }, [matrixData]);

  const cellColor = (cell) => {
    if (!cell) return 'bg-slate-100 text-slate-400';
    if (cell.stop_sell_flag) return 'bg-slate-200 text-slate-500';
    const avail = Number(cell.available_inventory || 0);
    const physical = Number(cell.physical_inventory || 1);
    if (avail <= 0) return 'bg-red-100 text-red-600';
    if (avail / physical <= 0.3) return 'bg-amber-100 text-amber-700';
    return 'bg-green-100 text-green-700';
  };

  const openEdit = (rt, date) => {
    const cell = getCell(rt.room_type_id, date);
    if (!cell) return;
    setEditForm({
      out_of_service: Number(cell.out_of_service || 0), house_use: Number(cell.house_use || 0),
      overbooking_limit: Number(cell.overbooking_limit || 0),
      stop_sell_flag: Boolean(cell.stop_sell_flag), cta_flag: Boolean(cell.cta_flag), ctd_flag: Boolean(cell.ctd_flag),
      min_los: Number(cell.min_los || 1), max_los: Number(cell.max_los || 0), reason: ''
    });
    setEditModal({ roomTypeId: rt.room_type_id, roomTypeName: rt.room_type_name, date, row: cell });
  };

  const saveEdit = async () => {
    if (!editModal) return;
    setEditBusy(true); setError('');
    try {
      const res = await fetch('/api/v1/pms/inventory/controls', {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ room_type_id: editModal.roomTypeId, business_date: editModal.date, ...editForm })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Update failed');
      setMessage('Controls updated successfully'); setEditModal(null); loadMatrix();
    } catch (err) { setError(err?.message || 'Update failed'); }
    finally { setEditBusy(false); }
  };

  const openBulk = () => {
    setBulkForm({ room_type_id: '', start_date: startDate, end_date: endDate, day_filter: 'all', reason: '',
      stop_sell_flag: false, cta_flag: false, ctd_flag: false,
      overbooking_limit: 0, out_of_service: 0, house_use: 0, min_los: 1, max_los: 0 });
    setBulkModal(true);
  };

  const saveBulk = async () => {
    setBulkBusy(true); setError('');
    try {
      const controls = {};
      for (const f of ['out_of_service','house_use','overbooking_limit','stop_sell_flag','cta_flag','ctd_flag','min_los','max_los']) controls[f] = bulkForm[f];
      const res = await fetch('/api/v1/pms/inventory/bulk-controls', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ room_type_id: bulkForm.room_type_id ? Number(bulkForm.room_type_id) : null,
          start_date: bulkForm.start_date, end_date: bulkForm.end_date, day_filter: bulkForm.day_filter, controls, reason: bulkForm.reason || 'Bulk update' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Bulk update failed');
      setMessage(`Bulk update applied to ${data.updated_rows} rows`); setBulkModal(false); loadMatrix();
    } catch (err) { setError(err?.message || 'Bulk update failed'); }
    finally { setBulkBusy(false); }
  };

  const loadAudit = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ from: auditFilter.from || toIsoDate(addDays(new Date(), -30)), to: auditFilter.to || toIsoDate(new Date()), limit: '50', offset: String(auditPage * 50) });
      if (auditFilter.room_type_id) params.set('room_type_id', auditFilter.room_type_id);
      if (auditFilter.source) params.set('source', auditFilter.source);
      const res = await fetch(`/api/v1/pms/inventory/audit-log?${params}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed');
      setAuditRows(data.rows || []); setAuditTotal(data.total || 0);
    } catch (err) { setError(err?.message || 'Failed to load audit'); }
    finally { setAuditLoading(false); }
  }, [selectedProperty, auditFilter, auditPage, headers]);

  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab, loadAudit]);

  const handleRebuild = async () => {
    if (!window.confirm('Rebuild inventory for this property? This may take a moment.')) return;
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/v1/pms/inventory/rebuild', { method: 'POST', headers: headers(), body: JSON.stringify({ start_date: startDate, end_date: endDate }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Rebuild failed');
      setMessage(`Rebuilt ${data.rows} inventory rows`); loadMatrix();
    } catch (err) { setError(err?.message || 'Rebuild failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (message) { const t = setTimeout(() => setMessage(''), 4000); return () => clearTimeout(t); } }, [message]);

  /* ── tab button (matches PmsMastersHub pattern) ── */
  const tabBtn = (active) => ({
    padding: '0.55rem 0.85rem', borderRadius: '7px', border: '1px solid #cbd5e0',
    background: active ? 'var(--theme-primary)' : '#fff', color: active ? '#fff' : '#1f2937', cursor: 'pointer'
  });

  if (!selectedProperty?.id) {
    return <div className="page-shell"><p style={{ color: '#6b7280' }}>Select a property first.</p></div>;
  }

  /* ── shared controls form (edit & bulk modals) ── */
  const renderControlFields = (form, setFn) => (
    <>
      <div className="filter-bar" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem', padding: '0.8rem' }}>
        <label><span className="form-label">Out of Service</span>
          <input type="number" min={0} value={form.out_of_service} onChange={e => setFn({ ...form, out_of_service: Number(e.target.value) })} className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label><span className="form-label">House Use</span>
          <input type="number" min={0} value={form.house_use} onChange={e => setFn({ ...form, house_use: Number(e.target.value) })} className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label><span className="form-label">Overbooking Limit</span>
          <input type="number" min={0} value={form.overbooking_limit} onChange={e => setFn({ ...form, overbooking_limit: Number(e.target.value) })} className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label><span className="form-label">Min LOS</span>
          <input type="number" min={1} value={form.min_los} onChange={e => setFn({ ...form, min_los: Number(e.target.value) })} className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label><span className="form-label">Max LOS</span>
          <input type="number" min={0} value={form.max_los} onChange={e => setFn({ ...form, max_los: Number(e.target.value) })} className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
          <span className="text-xs text-slate-400">0 = unlimited</span>
        </label>
      </div>
      <div className="action-bar" style={{ marginTop: '0.6rem' }}>
        {[['Stop Sell','stop_sell_flag'],['Close to Arrival (CTA)','cta_flag'],['Close to Departure (CTD)','ctd_flag']].map(([lbl,fld]) => (
          <label key={fld} className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={form[fld] || false} onChange={e => setFn({ ...form, [fld]: e.target.checked })} className="w-4 h-4 cursor-pointer" />
            {lbl}
          </label>
        ))}
      </div>
      <label className="block mt-2"><span className="form-label">Reason (optional)</span>
        <input type="text" value={form.reason || ''} onChange={e => setFn({ ...form, reason: e.target.value })} placeholder="e.g. Renovation, Event block..." className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
      </label>
    </>
  );

  return (
    <div className="page-shell">
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Room Inventory</h3>

      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      {/* Tabs */}
      <div className="action-bar">
        <button onClick={() => setTab('grid')} style={tabBtn(tab === 'grid')}>Calendar Grid</button>
        <button onClick={() => setTab('audit')} style={tabBtn(tab === 'audit')}>Audit Log</button>
      </div>

      {/* ══════════ GRID TAB ══════════ */}
      {tab === 'grid' && (
        <>
          <div className="filter-bar" style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label><span className="form-label">Start Date</span>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </label>
            <label><span className="form-label">Days</span>
              <select value={days} onChange={e => setDays(Number(e.target.value))} className="rounded border border-slate-300 px-2 py-1 text-sm">
                <option value={7}>7 Days</option><option value={14}>14 Days</option><option value={21}>21 Days</option><option value={30}>30 Days</option>
              </select>
            </label>
            <button onClick={() => setStartDate(toIsoDate(addDays(startDate, -days)))} className="btn-muted btn-sm rounded">&larr; Prev</button>
            <button onClick={() => setStartDate(toIsoDate(new Date()))} className="btn-muted btn-sm rounded">Today</button>
            <button onClick={() => setStartDate(toIsoDate(addDays(startDate, days)))} className="btn-muted btn-sm rounded">Next &rarr;</button>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: 11, color: '#6b7280' }}>
              {lastUpdated && <span title="Last refreshed">Updated {lastUpdated.toLocaleTimeString()}</span>}
              <button onClick={() => loadMatrix(false)} className="btn-muted btn-sm rounded" title="Refresh now" aria-label="Refresh now">&#8635;</button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} title="Auto-refresh every 30 seconds while this tab is open">
                <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
                <span style={{ color: autoRefresh ? '#166534' : '#94a3b8', fontWeight: 600 }}>{autoRefresh ? '● Live' : 'Live'}</span>
              </label>
            </div>
            <button onClick={openBulk} className="btn-primary btn-sm rounded">Bulk Update</button>
            <button onClick={handleRebuild} className="btn-muted btn-sm rounded">Recalculate</button>
          </div>

          {/* Legend + Formula */}
          <div className="action-bar" style={{ fontSize: 11, flexWrap: 'wrap' }}>
            <span className="tag tag-success">Available</span>
            <span className="tag tag-warning">Low (&le;30%)</span>
            <span className="tag tag-danger">Sold Out</span>
            <span className="tag tag-muted">Stop Sell</span>
            <span style={{ color: '#6b7280', marginLeft: '0.5rem', borderLeft: '1px solid #d1d5db', paddingLeft: '0.5rem' }}>
              Sell = Physical &minus; OOO &minus; OOS &minus; H.Use &nbsp;|&nbsp; Avail = Sell &minus; Rsvd + Overbooking
            </span>
          </div>

          {loading ? <div className="text-center text-slate-400 py-8">Loading inventory...</div> : (
            roomTypes.length === 0 ? (
              <div className="text-center text-slate-400 py-8">No inventory data. Click <b>Recalculate</b> to build inventory rows.</div>
            ) : (
              <div className="card" style={{ overflowX: 'auto' }}>
                <table className="pms-grid" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '6px 10px', textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, minWidth: 140 }}>Room Type</th>
                      <th style={{ padding: '6px 6px', textAlign: 'center', minWidth: 40, fontSize: 11, color: '#6b7280' }}>Metric</th>
                      {dateColumns.map(d => (
                        <th key={d} style={{ padding: '4px 2px', textAlign: 'center', minWidth: 54 }}>
                          <div className="text-xs text-slate-400">{dayLabel(d)}</div>
                          <div style={{ fontSize: 11 }}>{fmtDate(d)}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {roomTypes.map(rt => {
                      const metrics = [
                        { key: 'phys', label: 'Physical', field: 'physical_inventory', color: 'var(--theme-text)' },
                        { key: 'ooo', label: 'OOO', field: 'out_of_order', color: 'var(--theme-danger)' },
                        { key: 'oos', label: 'OOS', field: 'out_of_service', color: '#ea580c' },
                        { key: 'hu', label: 'H.Use', field: 'house_use', color: '#9333ea' },
                        { key: 'sell', label: 'Sell', field: 'sellable_inventory', color: '#2563eb' },
                        { key: 'rsv', label: 'Rsvd', field: 'reserved', color: 'var(--theme-warning)' },
                        { key: 'avail', label: 'Avail', field: 'available_inventory', color: '#059669' },
                      ];
                      return metrics.map((m, mi) => (
                        <tr key={`${rt.room_type_id}-${m.key}`} style={{ borderBottom: mi === metrics.length - 1 ? '2px solid var(--theme-border)' : '1px solid #f3f4f6' }}>
                          {mi === 0 && (
                            <td rowSpan={metrics.length} style={{ padding: '6px 10px', fontWeight: 600, verticalAlign: 'top', position: 'sticky', left: 0, background: '#fff', zIndex: 1, borderBottom: '2px solid var(--theme-border)', minWidth: 140 }}>
                              {rt.room_type_name}
                              <div className="text-xs text-slate-400" style={{ fontWeight: 400 }}>{rt.room_type_code}</div>
                            </td>
                          )}
                          <td className="text-center text-xs" style={{ padding: '3px 6px', background: '#fafbfc', color: m.color || '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>{m.label}</td>
                          {dateColumns.map(d => {
                            const cell = getCell(rt.room_type_id, d);
                            const val = cell ? Number(cell[m.field] || 0) : '-';
                            const colorCls = m.key === 'avail' ? cellColor(cell) : '';
                            return (
                              <td key={d} style={{ padding: '2px' }}>
                                <div
                                  onClick={() => openEdit(rt, d)}
                                  className={`text-center rounded cursor-pointer text-xs font-semibold px-1 py-0.5 ${m.key === 'avail' ? colorCls : ''}`}
                                  style={{ minWidth: 40, ...(cell?.stop_sell_flag && m.key === 'avail' ? { textDecoration: 'line-through' } : {}) }}
                                  title={`${rt.room_type_name} | ${fmtDateFull(d)} | Click to edit`}
                                >
                                  {val}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })}
                    {/* Totals rows */}
                    {[
                      { key: 'phys', label: 'Physical', fn: (d) => roomTypes.reduce((sum, rt) => { const c = getCell(rt.room_type_id, d); return sum + Number(c?.physical_inventory || 0); }, 0) },
                      { key: 'avail', label: 'Avail', fn: (d) => { const t = matrixData?.totals?.find(t => t.date === d); return t ? t.avail : '-'; } }
                    ].map(tot => (
                      <tr key={tot.key} style={{ background: 'var(--theme-header-bg)', fontWeight: 700 }}>
                        {tot.key === 'phys' && <td rowSpan={2} style={{ padding: '6px 10px', position: 'sticky', left: 0, background: 'var(--theme-header-bg)', zIndex: 1 }}>TOTAL</td>}
                        <td className="text-center text-xs text-slate-500" style={{ padding: '3px 6px', background: 'var(--theme-header-bg)' }}>{tot.label}</td>
                        {dateColumns.map(d => (
                          <td key={d} className="text-center text-xs" style={{ padding: '2px' }}>{tot.fn(d)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}

      {/* ══════════ AUDIT TAB ══════════ */}
      {tab === 'audit' && (
        <>
          <div className="filter-bar" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label><span className="form-label">From</span>
              <input type="date" value={auditFilter.from || toIsoDate(addDays(new Date(), -30))}
                onChange={e => { setAuditFilter(syncDateRange({ ...auditFilter, from: e.target.value }, 'from', 'to', 'from')); setAuditPage(0); }} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </label>
            <label><span className="form-label">To</span>
              <input type="date" value={auditFilter.to || toIsoDate(new Date())}
                onChange={e => { setAuditFilter(syncDateRange({ ...auditFilter, to: e.target.value }, 'from', 'to', 'to')); setAuditPage(0); }} className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </label>
            <label><span className="form-label">Room Type</span>
              <select value={auditFilter.room_type_id}
                onChange={e => { setAuditFilter({ ...auditFilter, room_type_id: e.target.value }); setAuditPage(0); }} className="rounded border border-slate-300 px-2 py-1 text-sm">
                <option value="">All</option>
                {roomTypes.map(rt => <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name}</option>)}
              </select>
            </label>
            <label><span className="form-label">Source</span>
              <select value={auditFilter.source}
                onChange={e => { setAuditFilter({ ...auditFilter, source: e.target.value }); setAuditPage(0); }} className="rounded border border-slate-300 px-2 py-1 text-sm">
                <option value="">All</option>
                <option value="MANUAL">Manual</option>
                <option value="SYSTEM_RECALC">System</option>
                <option value="CM_PUSH">Channel Manager</option>
              </select>
            </label>
            <button onClick={loadAudit} className="btn-primary btn-sm rounded">Search</button>
          </div>

          {auditLoading ? <div className="text-center text-slate-400 py-8">Loading...</div> : (
            <div className="card" style={{ overflowX: 'auto' }}>
              <div className="card-body">
                <div className="text-xs text-slate-400 mb-1">Showing {auditRows.length} of {auditTotal} entries</div>
                <table className="pms-grid" style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Timestamp</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Room Type</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Field</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>Old</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>New</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Source</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>User</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditRows.length === 0 ? (
                      <tr><td colSpan={9} className="text-center text-slate-400 py-6">No audit entries found</td></tr>
                    ) : auditRows.map(row => (
                      <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td className="px-2 py-1 text-xs text-slate-500">
                          {fmtDateTimeShort(row.created_at)}
                        </td>
                        <td className="px-2 py-1 text-xs">{row.room_type_name || row.room_type_id}</td>
                        <td className="px-2 py-1 text-xs">{fmtDate(row.business_date)}</td>
                        <td className="px-2 py-1 text-xs font-mono">{row.field_name}</td>
                        <td className="px-2 py-1 text-xs text-center text-red-600">{row.old_value}</td>
                        <td className="px-2 py-1 text-xs text-center text-green-600 font-semibold">{row.new_value}</td>
                        <td className="px-2 py-1">
                          <span className={`tag ${row.change_source === 'MANUAL' ? 'tag-info' : row.change_source === 'SYSTEM_RECALC' ? 'tag-primary' : 'tag-warning'}`}>
                            {row.change_source}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-xs">{row.actor_name || '-'}</td>
                        <td className="px-2 py-1 text-xs text-slate-500 truncate" style={{ maxWidth: 180 }}>{row.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {auditTotal > 50 && (
                  <div className="action-bar" style={{ marginTop: '0.5rem' }}>
                    <button onClick={() => setAuditPage(p => Math.max(0, p - 1))} disabled={auditPage === 0} className="btn-muted btn-sm rounded">&larr; Prev</button>
                    <span className="text-xs text-slate-400">Page {auditPage + 1} of {Math.ceil(auditTotal / 50)}</span>
                    <button onClick={() => setAuditPage(p => p + 1)} disabled={(auditPage + 1) * 50 >= auditTotal} className="btn-muted btn-sm rounded">Next &rarr;</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════ EDIT MODAL ══════════ */}
      {editModal && (
        <div style={overlayStyle} onClick={() => setEditModal(null)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <button onClick={() => setEditModal(null)} style={closeBtnStyle}>&times;</button>
            <h4 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>{editModal.roomTypeName} &mdash; {fmtDateFull(editModal.date)}</h4>

            {/* Read-only metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)', gap: '0.4rem' }}>
              {[['Physical', editModal.row.physical_inventory], ['OOO', editModal.row.out_of_order], ['Reserved', editModal.row.reserved], ['Arrivals', editModal.row.arrivals],
                ['Departures', editModal.row.departures], ['Stayovers', editModal.row.stayovers], ['Sellable', editModal.row.sellable_inventory], ['Available', editModal.row.available_inventory]
              ].map(([lbl, val]) => (
                <div key={lbl}>
                  <span className="form-label">{lbl}</span>
                  <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1 text-sm text-slate-600">{val ?? 0}</div>
                </div>
              ))}
            </div>

            <div className="section-head" style={{ marginTop: '1rem' }}>Inventory Controls</div>
            {renderControlFields(editForm, setEditForm)}

            <div className="action-bar" style={{ marginTop: '1rem' }}>
              <button onClick={saveEdit} disabled={editBusy} className="btn-primary btn-sm rounded">{editBusy ? 'Saving...' : 'Save'}</button>
              <button onClick={() => setEditModal(null)} className="btn-muted btn-sm rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ BULK MODAL ══════════ */}
      {bulkModal && (
        <div style={overlayStyle} onClick={() => setBulkModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <button onClick={() => setBulkModal(false)} style={closeBtnStyle}>&times;</button>
            <h4 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Bulk Inventory Update</h4>

            <div className="filter-bar" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem', padding: '0.8rem' }}>
              <label><span className="form-label">Room Type</span>
                <select value={bulkForm.room_type_id} onChange={e => setBulkForm({ ...bulkForm, room_type_id: e.target.value })} className="w-full rounded border border-slate-300 px-2 py-1 text-sm">
                  <option value="">All Room Types</option>
                  {roomTypes.map(rt => <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name} ({rt.total_rooms} rooms)</option>)}
                </select>
              </label>
              <label><span className="form-label">Day Filter</span>
                <select value={bulkForm.day_filter} onChange={e => setBulkForm({ ...bulkForm, day_filter: e.target.value })} className="w-full rounded border border-slate-300 px-2 py-1 text-sm">
                  <option value="all">All Days</option><option value="weekdays">Weekdays Only</option><option value="weekends">Weekends Only</option>
                </select>
              </label>
              <label><span className="form-label">Start Date</span>
                <input type="date" value={bulkForm.start_date} onChange={e => setBulkForm(syncDateRange({ ...bulkForm, start_date: e.target.value }, 'start_date', 'end_date', 'start_date'))} className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
              </label>
              <label><span className="form-label">End Date</span>
                <input type="date" value={bulkForm.end_date} min={bulkForm.start_date || undefined} onChange={e => setBulkForm(syncDateRange({ ...bulkForm, end_date: e.target.value }, 'start_date', 'end_date', 'end_date'))} className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
              </label>
            </div>

            {/* Total Inventory summary */}
            {(() => {
              const selectedRt = bulkForm.room_type_id ? roomTypes.find(rt => String(rt.room_type_id) === String(bulkForm.room_type_id)) : null;
              const totalRooms = selectedRt ? selectedRt.total_rooms : roomTypes.reduce((sum, rt) => sum + rt.total_rooms, 0);
              const label = selectedRt ? selectedRt.room_type_name : 'All Room Types';
              return (
                <div className="info-banner" style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                  <span><b>{label}</b></span>
                  <span>Total Inventory: <b>{totalRooms}</b> rooms</span>
                </div>
              );
            })()}

            <div className="section-head" style={{ marginTop: '0.8rem' }}>Controls to Apply</div>
            {renderControlFields(bulkForm, setBulkForm)}

            <div className="action-bar" style={{ marginTop: '1rem' }}>
              <button onClick={saveBulk} disabled={bulkBusy} className="btn-primary btn-sm rounded">{bulkBusy ? 'Applying...' : 'Apply Bulk Update'}</button>
              <button onClick={() => setBulkModal(false)} className="btn-muted btn-sm rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PmsRoomInventory;
